/**
 * Daily Signals Feed — shared, pure model (no I/O). Types + deterministic helpers used by both the
 * server (emit from generators, the /signals API, lifecycle) and the client (the `DaySignals` feed
 * rendered below each day's journal). Signals are machine-owned notifications that live OUTSIDE the
 * user's Yjs journal doc (spec D2); this module never touches persistence — the stores + `SignalService`
 * live in `server-api/src/signals/`.
 *
 * See `docs/plans/daily-signals-feed.md` (spec, §1 decisions D1–D22) and
 * `docs/plans/daily-signals-decision-log.md` (implementation decisions IMPL-*).
 */

import type { AlertKind } from '../financial/types.js';
import { domainOf, isFreemailDomain } from '../engines/spamSignals.js';

/**
 * The generators shipping in v1 (spec D8). Extensible — SIG-07 adds `recurring-charge`.
 *
 * The `*-found` / `pending-delivery` / `task-nudge` kinds come from the tracking + deadline generators.
 * `bill-due` / `task-added` / `note-added` are the email/calendar ENGINE generators: every engine that
 * takes a user-visible action (captures a bill, creates a task, writes a note into the journal) surfaces
 * it here, so the engine pipeline and the Signals feed are one system — the feed is the canonical
 * "here's what Lapo did" surface, while the note/task it points at stays the durable artifact.
 */
export type SignalKind =
  | 'tracking-found'
  | 'pending-delivery'
  | 'task-nudge'
  | 'bill-due'
  | 'task-added'
  | 'note-added'
  // Email/Calendar triage → actions (spec A5). `action-proposed` = an approvable write Lapo prepared
  // (draft reply, archive, RSVP); `action-escalated` = the residue that needs the owner's judgment;
  // `triage-audit` = the single collapsed "Lapo handled N ▸" line for auto-handled items (spec A4).
  | 'action-proposed'
  | 'action-escalated'
  | 'triage-audit'
  // Financial Management (FIN): a notable money event the nightly sync flagged — a large charge, a new
  // or duplicate recurring subscription, a price increase, or a bank connection that needs reconnecting.
  // These used to be emailed; they now surface here (money-free wording — see the Signal doc, spec D21).
  | 'financial-alert'
  // RECON R5 — a high-importance Slack message that needs your reply, promoted from the nightly digest to
  // a first-class closeable card. Auto-closes when you reply in-thread or react (R6).
  | 'slack-needs-reply'
  // AXIS (X18/§3.5) — a time-sensitive graduation proposal card ("You've approved 5/5 Calendly
  // archives across 3 days — auto-archive from now on?"). entityRef = the TrustRule.ruleKey.
  | 'graduation-proposal'
  // AXIS (X4/§3.5, IMPL-AX-26) — the once-daily "here's everything I did on my own" recap. Declared for
  // schema stability; v1 renders the recap in the Autonomy panel and does not emit this into the feed.
  | 'autonomy-recap'
  // LEDGER (D11/D26d) — the morning "N decisions logged overnight — review" card, deep-linking to the
  // Decision Ledger view. Emitted once per (userId, dateKey), never when N=0.
  | 'decisions-logged'
  // LEDGER (D23) — a decision's stated/owner-set `revisitAt` has arrived; fires once per decision
  // (guarded by `revisitNotifiedAt`), deep-linking to that decision.
  | 'decision-revisit-due'
  // WEAVE — an open decision gained new cross-channel follow-up activity (or a status change) overnight:
  // "Your decision to hire Jane had 3 updates across #hiring, a DM, and email." One card per
  // (decisionId, dateKey), only when net-new updates landed; entityRef = decisionId, deep-links the panel.
  | 'decision-updated'
  // DIAL (D8) — the outcome of an outbound voice call Lapo placed. `call-completed` carries the Claude
  // summary + structured outcome (reservation confirmed etc.); `call-failed` is the low-priority
  // voicemail/no-answer/failure notice with a Retry action. entityRef = the Lapo callId.
  | 'call-completed'
  | 'call-failed'
  // ECHO (D4/D10) — the weekly "forgotten but relevant" digest: 3–5 dormant documents/decisions
  // semantically close to the current active context, each with a one-line "why now". One card per
  // ISO week (dedupeKey `resurface-digest:{isoWeek}`), info priority, 7-day TTL; silent weeks emit
  // nothing (no filler). See docs/plans/note-resurfacing.md.
  | 'resurface-digest'
  // SIEVE (S8/S9) — ingest triage. `ingest-filtered` is the collapsed daily "Lapo filtered N shared
  // items ▸" card over park verdicts (one-tap restore); `ingest-think` marks an ingested share as
  // "worth a think" with an open-note action. See docs/plans/ingest-triage.md.
  | 'ingest-filtered'
  | 'ingest-think'
  // TUNE (U6) — the weekly "What Lapo learned" digest: guidance entries the feedback distiller
  // added/merged/retired this week, deep-linking to the Guidance Ledger. See docs/plans/signal-feedback.md.
  | 'guidance-digest'
  // TUNE (U7) — a distiller-emitted routing proposal ("Stop showing X?" yes/no) after a not-useful
  // cluster; accepting writes the RoutingRule via the EXISTING routing machinery. Never silent.
  | 'routing-proposal'
  // CREEL (D10) — an Amazon order was placed. Emitted immediately for every auto-confirmed order (the
  // one moment the system spends alone is never quiet); optional for other surfaces. sourceRef carries
  // the intentId + orderId.
  | 'order-placed'
  // CREEL (§9) — Clavero needs a human (423 CAPTCHA/challenge), is HALTed, an intent aborted on drift,
  // or the nightly job degraded to stale data. Urgent; deduped per cause while it persists.
  | 'shopping-attention'
  // CREEL (D14) — the Monday weekly digest: "N Amazon charges need matching", deep-linking the finance
  // view. Emitted once per ISO week only when the ambiguous count > 0.
  | 'amazon-match-digest'
  // REMIT (Bill Pay, D12/§4.8) — a completed payment: "Paid {payee} {amount} — confirmation {code}".
  // Info priority; entityRef = billId. Emitted once when a bill flips to `paid`.
  | 'bill-paid'
  // TEMPER (T-D10) — the machine deliberately stopped on a bill (hard wall / stalled / exhausted
  // campaign) and a human is needed: the hand-off card. Urgent for card-declined or due ≤ T-5,
  // action otherwise; entityRef = billId (one card per bill, updated on later transitions).
  | 'bill-needs-you'
  // SENTRY — a user/agent-authored watch rule fired on an incoming event (email/calendar/financial/
  // shipment). Bound to the triggering source so it reconciles/clears like any other bound card; the
  // card reason carries the rule's label ("matched your watch: bills over $500").
  | 'watch-fired'
  // RELAY — a persistent Job hit a human decision and halted (DL-R3 give-up / DL-R7 hard-floor). Bound to
  // the Job; clears when the Job leaves needs-review (answered / cancelled).
  | 'job-needs-review'
  // KUDOS — a released "write a thank-you to X" suggestion. Bound to the suggestion; auto-resolves when
  // the letter is written (acted) or the suggestion is dismissed. entityRef = the employee's orbitEntityKey.
  | 'kudos-suggested'
  // KUDOS — a low-stakes "I found a Thank-You Card for an ambiguous name — who is this?" confirm nudge
  // from the nightly out-of-band reconcile (DL-19). entityRef = the unmatched first name.
  | 'kudos-reconcile';

/** urgent → action → info (spec D12 ordering; only urgent/action roll forward — D5). */
export type SignalPriority = 'info' | 'action' | 'urgent';

/**
 * Lifecycle (spec D14): a signal starts `active`, may be `seen` (seenAt, still active), then terminal.
 * `resolved` (RECON R9) is a distinct terminal state from `acted` (you did the primary action) and
 * `dismissed` (you rejected it): the signal auto-closed because you handled its SOURCE out-of-band —
 * replied to the email/Slack thread, completed the linked Todoist task, or the calendar event passed.
 * `set-aside` (HONE) is the neutral "✕ / I'm not sure" — the owner hid the card WITHOUT judging its
 * relevance, so unlike `dismissed` it is EXCLUDED from noise tuning (mirrors `resolved`).
 */
export type SignalStatus = 'active' | 'snoozed' | 'dismissed' | 'acted' | 'resolved' | 'set-aside';

/**
 * Why a signal auto-`resolved` (RECON R9). Recorded on the signal + shown in the "closed" toast copy.
 * Deliberately resolution-only: a Todoist *deletion* is a dismissal (R12), tracked via `dismissedReason`.
 */
export type ResolutionReason =
  | 'replied' //          email/slack reply (R2/R6)
  | 'forwarded' //        owner forwarded the email thread — handed off out-of-band (R2)
  | 'reacted' //          slack emoji reaction (R6)
  | 'task-completed' //   linked Todoist task closed (R8)
  | 'event-passed' //     calendar start time elapsed (R4)
  | 'event-cancelled' //  calendar event cancelled (R4)
  | 'rsvp' //             attendee responseStatus set (R4)
  | 'email-archived' //   source email left the inbox (archived/trashed) — you've dealt with it
  | 'alert-dismissed' //  MIRROR — the finance alert was cleared on the Finance page (spec §9.2)
  | 'txn-categorized' //  MIRROR — the flagged transaction was justified/categorized (spec §9.3)
  // SETTLE — an ephemeral/recap card (a `triage-audit` line, a weekly digest, a call recap) cleared
  // because the owner has now SEEN it (SETTLE-00, DL-6/DL-14). Silent — never surfaces a "closed" toast.
  | 'seen'
  // SETTLE — the shipment a `pending-delivery` card tracked was delivered (reconciled from the stored
  // tracking status, SETTLE-01/DL-12).
  | 'delivered'
  // SETTLE — the decision a `decision-*` card pointed at was closed/resolved in the ledger (SETTLE-01).
  | 'decision-closed'
  // SETTLE — the owner marked the source sender as spam; this card cleared as a retro-clear sibling
  // (SETTLE-04, DL-2/DL-16..19). The tapped card records `spam-marked`; siblings resolve silently.
  | 'spam-marked'
  | 'sender-suppressed';

/**
 * A Todoist task this signal spawned (RECON R8/R11). Completing ANY linked task resolves the signal;
 * a reply completes only the `'reply'`-intent tasks. `intent` is derived deterministically from the
 * task template/disposition (never an LLM call — spec §9).
 */
export interface LinkedTask {
  taskId: string;
  /** R11 — governs reply-completes-task: a follow-up/draft-reply task is `'reply'`; prep/generic is `'other'`. */
  intent: 'reply' | 'other';
  /** task-capture (engine auto-created) vs a tapped `create-task` action (audit only). */
  createdVia: 'engine' | 'action';
}

/**
 * PRISM — the Eisenhower verdict behind a created task's Todoist priority, recorded on the signal for
 * observability/tuning (spec §4.3, D12). `priority` is the resolved Todoist API value (1..4, 4 = UI P1);
 * `reason` is the model's one-line justification. Set once at creation; never re-derived (spec D4).
 */
export interface PriorityVerdict {
  urgent: boolean;
  important: boolean;
  /** Resolved Todoist API priority (1..4, 4 = urgent/UI P1). */
  priority: number;
  /** One short line on why (the model's `priorityReason`), when available. */
  reason?: string;
}

/**
 * HONE — the artifacts an owner can spawn FROM any signal via the card's ⋯ "Create" menu. One label per
 * outgoing action. `appointment` is a calendar event; `journal` appends to the day's journal; `document`
 * creates a standalone note; `call` places a Vapi call (floored, irreversible).
 */
export type CreateActionKind = 'draft' | 'task' | 'appointment' | 'document' | 'journal' | 'call';

/**
 * HONE — an artifact the owner created from a signal through the ⋯ menu. Recorded on the signal so the
 * menu's "Remove" section can show + undo it. Distinct from {@link LinkedTask} (which drives RECON watch
 * reconciliation): a `task` create mirrors into BOTH — `linkedTasks` for reconciliation, `linkedArtifacts`
 * for the Remove UI. `undoToken` is the executor's serialized inverse op (absent ⇒ not undoable, e.g. a
 * placed call or a journal append).
 */
export interface LinkedArtifact {
  /** The link-row id — what the Remove control targets (not the external ref). */
  artifactId: string;
  kind: CreateActionKind;
  /** External id: todoist taskId / gmail draftId / noteId / calendar eventId / callId. */
  ref: string;
  /** Human line for the ⋯ Remove section, e.g. "Task · Follow up with ACME". */
  label: string;
  /** Serialized inverse op (executor). Absent ⇒ the artifact cannot be undone (call / journal append). */
  undoToken?: string;
  /** `action` = a tapped ⋯-menu create; `engine` = an artifact an engine auto-created (audit only). */
  createdVia: 'action' | 'engine';
  createdAt: number;
}

/**
 * A first-class, indexable reference to the SOURCE a signal came from (RECON F4 → typed). The
 * reconciliation key: an inbound handled out-of-band (reply / RSVP / reaction / passage) is matched
 * back to its signal(s) through this ref via the `signalWatches` reverse index.
 */
export type SignalSourceRef =
  | {
      provider: 'email';
      accountId: string;
      threadId: string;
      messageId: string;
      /**
       * AXIS X29 — the Gmail draft id `createDraftReply` returned for this thread, stamped by the act
       * path so the draft's FATE is observable: a RECON reply-match diffs sent-vs-draft (draft-quality
       * learning), and a draft deleted unsent / gone stale records a discard negative (a strike at auto).
       */
      draftId?: string;
    }
  | { provider: 'calendar'; accountId: string; eventId: string; startsAt: number }
  | { provider: 'slack'; teamId: string; channelId: string; messageTs: string; threadTs?: string }
  // MIRROR (finance ↔ signal reconciliation, spec §9). A financial-alert signal watches its alert so a
  // dismiss on either surface clears the other; when the alert is tied to one transaction (`large-{txnId}`)
  // the `txnId` also lets categorizing that charge resolve the signal. `kind`/`txnId`/`streamId` are
  // carried so the reconciler acts without re-parsing the `alertId` string (DL-2).
  | { provider: 'financial'; alertId: string; kind: AlertKind; txnId?: string; streamId?: string }
  // CREEL — an Amazon/Clavero event. `intentId` on order-placed/shopping-attention (intent-scoped),
  // `orderId` on an order reference, `week` (ISO week key) on the weekly match digest.
  | { provider: 'clavero'; intentId?: string; orderId?: string; week?: string }
  // SETTLE-01 — a tracked shipment (`pending-delivery`/`tracking-found`). Reconciled from the STORED
  // tracking status (no new carrier polling — DL-12): the signal clears when the package is delivered.
  | { provider: 'shipment'; trackerId: string; carrier?: string; trackingCode?: string }
  // SETTLE-01 — a logged decision (`decision-*`). Clears when the decision is closed/resolved in the ledger.
  | { provider: 'decision'; decisionId: string }
  // RELAY — a persistent Job (`job-needs-review`). Clears when the Job leaves needs-review.
  | { provider: 'job'; jobId: string };

/**
 * Reverse index (RECON §4, own Firestore collection `signalWatches`): "for this source key, which live
 * signal(s) should auto-close when it's handled". Keyed per (source, signal) so several cards derived
 * from one email/event each watch it (IMPL-1/IMPL-2). Live only while the signal card is (mirrors
 * `expiresAt`, R13); GC'd on resolve or expiry.
 */
export interface SignalWatch {
  /** Composite doc id: `${watchKey}__${signalId}` (IMPL-2). */
  watchId: string;
  /** Typed source key — see the watch-key builders below. */
  watchKey: string;
  userId: string;
  signalId: string;
  dateKey: string;
  provider: 'email' | 'calendar' | 'slack' | 'todoist' | 'financial' | 'shipment' | 'decision' | 'job';
  sourceRef?: SignalSourceRef;
  /** Denormalized for the Todoist path — the linked task ids that (any) completion resolves. */
  linkedTaskIds: string[];
  /** Reply-intent subset of `linkedTaskIds` — the tasks a reply/reaction completes (R11). */
  replyTaskIds: string[];
  /** R13 — mirrors the signal's `expiresAt` (or a 5-day fallback, IMPL-3). */
  expiresAt: number;
  createdAt: number;
  updatedAt: number;
}

// --- Deterministic watch-key builders (RECON §4) — one key per source so late/dup events upsert. -----

export function emailWatchKey(accountId: string, threadId: string): string {
  return `email:${accountId}:${threadId}`;
}
export function calendarWatchKey(accountId: string, eventId: string): string {
  return `calendar:${accountId}:${eventId}`;
}
export function todoistWatchKey(taskId: string): string {
  return `todoist:${taskId}`;
}
export function slackWatchKey(teamId: string, channelId: string, messageTs: string): string {
  return `slack:${teamId}:${channelId}:${messageTs}`;
}
/** MIRROR — the primary watch key for a financial alert (dismiss-on-either-surface, spec §9.2). */
export function financialWatchKey(alertId: string): string {
  return `financial:${alertId}`;
}
/** MIRROR — the secondary watch key tying a financial signal to the transaction it flags (spec §9.3). */
export function financialTxnWatchKey(txnId: string): string {
  return `financial-txn:${txnId}`;
}
/** SETTLE-01 — watch key for a tracked shipment (clears on delivery). */
export function shipmentWatchKey(trackerId: string): string {
  return `shipment:${trackerId}`;
}
/** SETTLE-01 — watch key for a logged decision (clears when the decision closes). */
export function decisionWatchKey(decisionId: string): string {
  return `decision:${decisionId}`;
}

/** The watch key for a given source ref (email/calendar/slack/financial). Todoist keys on task id instead. */
export function watchKeyForSourceRef(ref: SignalSourceRef): string {
  switch (ref.provider) {
    case 'email':
      return emailWatchKey(ref.accountId, ref.threadId);
    case 'calendar':
      return calendarWatchKey(ref.accountId, ref.eventId);
    case 'slack':
      return slackWatchKey(ref.teamId, ref.channelId, ref.messageTs);
    case 'financial':
      return financialWatchKey(ref.alertId);
    case 'shipment':
      return shipmentWatchKey(ref.trackerId);
    case 'decision':
      return decisionWatchKey(ref.decisionId);
    case 'clavero':
      // CREEL signals are not out-of-band reconciled (no source "handled elsewhere" to watch); the
      // key exists only so the union is exhaustive. Never matched by a reconciler.
      return `clavero:${ref.intentId ?? ref.orderId ?? ref.week ?? 'x'}`;
    case 'job':
      return `job:${ref.jobId}`;
  }
}

/**
 * What a card's button does (spec D10 — actions are configured per template). `open-*`/`link` are pure
 * client navigation; `create-task`/`create-note` execute server-side on `act` (via existing services)
 * then navigate to the artifact; `snooze`/`dismiss` are lifecycle shortcuts.
 */
export type SignalActionKind =
  | 'open-note'
  | 'open-panel'
  | 'link'
  | 'create-task'
  | 'create-note'
  | 'snooze'
  | 'dismiss'
  // Email/Calendar write actions (spec A5). All execute server-side on `act` via GmailWriteService /
  // CalendarWriteService, each gated behind the owner's Approve tap in v1 (spec A1/A6). `send-draft` is
  // declared but disabled in v1 (drafts-first — spec A5/A8); it exists so a category can graduate later.
  | 'create-draft-reply'
  | 'send-draft'
  | 'archive'
  | 'label'
  | 'mark-read'
  // Mark-as-Spam (SPAM-*): archive + mark-read in one write, plus (server-side) teach the learning loop
  // so the sender's identity is auto-handled from now on. Reversible (no delete/trash).
  | 'mark-spam'
  // CULL — "Not spam" on an auto-archived message: unarchive it, cancel the provisional spam-pending
  // rule, and record a `rescued` negative example for the taste distiller.
  | 'rescue-spam'
  | 'rsvp'
  | 'propose-times'
  | 'unsubscribe'
  // AXIS X10/X20/X-20 — send an owner-approved Slack reply (via SlackReplyService, AS the user). HARD
  // FLOORED (X12): a graduated Slack rule auto-*prepares* the reply onto the card; the send itself is
  // only ever this explicit tap. Never auto-executes, never graduates.
  | 'slack-send'
  // REMIT (Bill Pay, D4/§4.3) — the owner's pre-approval tap: claim the idempotency guard, flip the
  // BillRecord → approved, and publish the pay job to the worker. Everything after this tap is
  // autonomous (deterministic guards replace any mid-flow pause). Payload carries { billId, paymentMethodId }.
  | 'pay-bill'
  // REMIT (Bill Pay, D16/§4.3) — cancel an auto-pay during its undo window: flips the BillRecord →
  // dismissed and releases the pay guard so nothing publishes. Payload carries { billId }.
  | 'cancel-bill'
  // SETTLE-04 (DL-2) — accept a domain-suppression graduation proposal: set a `domain:<domain>` spam
  // policy so ALL mail from that visible domain is auto-handled from now on. Payload carries { domain }.
  | 'suppress-domain';

/** Loosely-typed payload for a {@link SignalAction}; each `kind` reads the fields it needs. */
export interface SignalActionPayload {
  /** open-note: navigate to this note id … */
  noteId?: string;
  /** … or to this journal day (`YYYY-MM-DD`). */
  dateKey?: string;
  /** open-panel: the right-sidebar panel to open (e.g. `Shipments`). */
  panel?: string;
  /** link: external URL. */
  url?: string;
  /** snooze: how many days forward. */
  days?: number;
  /** create-task: the Todoist task to create on `act`. */
  taskContent?: string;
  taskDue?: string;
  /** create-note: the note stub to create on `act`. */
  noteTitle?: string;
  noteBody?: string;
  // --- Email/Calendar write actions (spec A5) --------------------------------------------------
  /** The linked account that owns the message/event (writes must target the right mailbox/calendar). */
  accountId?: string;
  /** create-draft-reply: the Gmail thread/message to reply under. */
  threadId?: string;
  messageId?: string;
  /** create-draft-reply / propose-times / unsubscribe: the drafted body (owner may edit before act). */
  draftBody?: string;
  draftSubject?: string;
  /**
   * QUILL (D2/D9) — why this draft was demoted to a confirm card instead of graduating silently:
   * residual AI-writing tells the repair pass couldn't clear, and/or a calendar conflict on a
   * proposed time. Rendered as a badge on the card. `blocking` conflicts (a proposed time that
   * overlaps an appointment — D9) disable one-click approve; style-only findings just badge.
   */
  styleFindings?: Array<{ ruleId: string; message: string }>;
  /** QUILL D12 — the ISO 8601 times this draft proposes (from the model, validated against the calendar). */
  proposedTimes?: string[];
  /** QUILL D9 — set when a proposed time conflicts with the calendar; the card hard-blocks approve. */
  timeConflict?: { proposedISO: string; conflictTitle?: string };
  /** label: the Gmail label to apply. */
  labelName?: string;
  /** rsvp: the response to set on the event. */
  rsvpResponse?: 'accepted' | 'declined' | 'tentative';
  /** rsvp / propose-times: the calendar event. */
  eventId?: string;
  /** The sender/domain this action is about — used by the inline quick-tune rules (spec A22). */
  sender?: string;
  /**
   * The sender-identity key a spam rule is learned on (SPAM-*) — the DKIM-validated `d=` domain when
   * authenticated, else `sender:<addr>`. Computed at emit time (needs the auth headers) so `onSpam` can
   * key the learned policy on it without re-parsing the message. See core `spamKeyFor`.
   */
  spamKey?: string;
  /** archive/label/mark-read batch (spec A14): apply the op to each of these messages. */
  batch?: Array<{ messageId: string; accountId?: string; labelName?: string }>;
  // --- Slack reply (AXIS X-20) ------------------------------------------------------------------
  /** slack-send: the workspace/channel/thread the approved reply posts into. */
  teamId?: string;
  channelId?: string;
  threadTs?: string;
  // --- ECHO resurface-digest (D4/D7) --------------------------------------------------------------
  /** One-line "why this matters now" for a resurfaced item (rendered under the item's title). */
  whyNow?: string;
  /**
   * The resurface suppression key for this item — the noteId, or `decision:{decisionId}` for a
   * resurfaced decision. The card's per-item "Not relevant" control posts it to
   * `POST /resurface/not-relevant` (a 180-day suppression), separate from the card lifecycle.
   */
  targetId?: string;
  // --- SIEVE ingest-filtered (S8) ------------------------------------------------------------------
  /** The parked-ingest id a per-item "Restore" control posts to `POST /ingest-triage/parked/:id/restore`. */
  parkedId?: string;
  // --- TUNE routing-proposal (U7) ------------------------------------------------------------------
  /** The routing rule the proposal's accept writes (`routingKeyForKindSender(kind, sender)`). */
  ruleKey?: string;
  // --- REMIT pay-bill / cancel-bill (Bill Pay, D4/D16) --------------------------------------------
  /** pay-bill / cancel-bill: the BillRecord this action pays or cancels. */
  billId?: string;
  /** pay-bill: the card metadata id to pay with (never a PAN — the worker resolves the secret). */
  paymentMethodId?: string;
  /** SETTLE-04 (DL-2) — suppress-domain: the visible sending domain to suppress wholesale (`acme.com`). */
  domain?: string;
}

export interface SignalAction {
  label: string;
  /** Exactly one action per signal should be primary (the prominent button). */
  primary?: boolean;
  kind: SignalActionKind;
  payload?: SignalActionPayload;
}

/**
 * A single machine-generated notification/insight, pinned to the day it fired on (`dateKey`, spec D5).
 * Money-free, plaintext, `userId`-partitioned (spec D21). Sensitive-but-not-secret (note titles,
 * tracking numbers), so no field-level encryption.
 */
export interface Signal {
  signalId: string;
  userId: string;
  /** The origin journal day this signal belongs to (`YYYY-MM-DD`). Immutable per-day log (spec D5). */
  dateKey: string;
  kind: SignalKind;
  /** Free-form provenance tag, e.g. `engine:tracking`, `poll:easypost`, `planner:deadline`. */
  source: string;
  priority: SignalPriority;
  title: string;
  detail: string;
  /** One-line "why did this fire" shown on every card (spec D13). */
  reason: string;
  actions: SignalAction[];
  status: SignalStatus;
  /** Set iff `status==='resolved'` — why it auto-closed (RECON R9). Drives the "closed" toast copy. */
  resolutionReason?: ResolutionReason;
  /** Epoch ms the signal auto-resolved (RECON R9). */
  resolvedAt?: number;
  /** Why a reconciliation `dismiss`ed this signal (RECON R12) — currently `'task-deleted'` (IMPL-4). */
  dismissedReason?: string;
  /** RECON R2/R4/R6 — typed source ref this signal reconciles against (email thread / event / slack msg). */
  sourceRef?: SignalSourceRef;
  /** RECON R8 — every Todoist task this signal spawned; any completion resolves it. */
  linkedTasks?: LinkedTask[];
  /** PRISM — the Eisenhower verdict behind the created task's priority (observability, spec D12). */
  priorityVerdict?: PriorityVerdict;
  /** HONE — every artifact the owner created from this signal via the ⋯ menu (drives the Remove section). */
  linkedArtifacts?: LinkedArtifact[];
  /** Set once the card has been scrolled into view + dwelled (spec D14); distinct from dismissed. */
  seenAt?: number;
  /**
   * SETTLE-00 — how many DISTINCT viewing sessions this card has had (debounced in `markSeen` by
   * {@link SEEN_RECOUNT_MS}). Drives ephemeral clear-on-seen: tier-A recaps clear at 1, tier-B at 2 (a
   * single scroll-by never retires a high-value recap — DL-14). Absent ⇒ never seen.
   */
  seenCount?: number;
  /** SETTLE-00 — the most recent `markSeen` time; used only to debounce `seenCount` across loads. */
  lastSeenAt?: number;
  /**
   * SETTLE — the sender-identity key this card is attributed to (`spamKeyFor` output: `dkim:<domain>`
   * or `sender:<addr>`). Stamped at emit for bound email cards so marking one sender spam can retro-clear
   * every other live card from the same identity (SETTLE-04, DL-16). Absent for non-email signals.
   */
  identityKey?: string;
  /**
   * HELM — the stable `${provider}:${stableId}` key of the SUBJECT this signal is about (a thread,
   * a bill, a person, a decision). A SIBLING of {@link identityKey}, never a replacement (DL-1):
   * identityKey is a sender identity (spam retro-clear), subjectKey is the thing. Stamped by the
   * subject resolver (P1-2) before upsert; backs `SignalStore.listBySubject` + the subject ledger.
   */
  subjectKey?: string;
  /**
   * HELM — a producer-supplied hash of the signal's MATERIAL content, used by the terminal law to
   * tell "same news" from "new news" without a byte-exact title/detail/actions compare (P1-3,
   * DL-4/DL-6). Absent ⇒ the terminal law falls back to the normalized title + detail + actions rule.
   */
  materialHash?: string;
  /** Epoch ms until which a snoozed signal stays hidden; on/after this it resurfaces. */
  snoozedUntil?: number;
  /** AXIS X16 — when the current snooze began (set on snooze, cleared on resurface). */
  snoozedAtMs?: number;
  /**
   * AXIS X16 — TOTAL time this signal has spent snoozed, epoch-ms duration. The unhandled→task grace
   * clock subtracts it ("snooze pauses the clock; the safety net re-arms on resurface").
   */
  snoozeAccumMs?: number;
  /** Kind-specific TTL (spec D17): on/after this the signal is treated as expired (auto-dismissed). */
  expiresAt?: number;
  /**
   * When the underlying subject occurs/occurred, epoch ms — a calendar event's start, or an email's
   * received time. Distinct from `createdAt` (when the signal was generated). Shown on the card so the
   * owner knows when the appointment is / when the mail arrived. Absent for signals with no natural time.
   */
  occurredAt?: number;
  /** Idempotency key (spec D9) — `emit` upserts by this; deterministic per (kind, entity). */
  dedupeKey: string;
  /** Opaque id of the subject (trackerId / noteId / taskId) — used to build `dedupeKey` + link. */
  entityRef?: string;
  createdAt: number;
  updatedAt: number;
}

/** Per-user, per-kind engagement counters (spec D18) — the data that later gating (SIG-07) tunes on. */
export interface MetricCounts {
  emitted: number;
  seen: number;
  acted: number;
  dismissed: number;
  snoozed: number;
  // TUNE U10 — the explicit-feedback counters (the uniform capture layer).
  useful: number;
  notUseful: number;
  actionGood: number;
  actionBad: number;
  /**
   * RECON R10 — auto-close outcomes, recorded for audit/metrics but EXCLUDED from noise-tuning /
   * autonomy-graduation inputs (see {@link isTuningNegativeSignal}), so auto-close never skews what
   * surfaces. Distinct from `dismissed` (a user "not useful" negative that DOES tune).
   */
  resolved: number;
  /**
   * HONE — the neutral "✕ / set aside" count. Like `resolved` it is recorded for audit but EXCLUDED
   * from noise tuning (the owner hid the card without judging its relevance — "I'm not sure").
   */
  setAside: number;
}

export interface SignalMetrics extends MetricCounts {
  kind: SignalKind;
  updatedAt: number;
}

export function emptyMetricCounts(): MetricCounts {
  return { emitted: 0, seen: 0, acted: 0, dismissed: 0, snoozed: 0, useful: 0, notUseful: 0, actionGood: 0, actionBad: 0, resolved: 0, setAside: 0 };
}

/** The metric event names — one per lifecycle transition worth measuring. `resolved` is RECON R9. */
export type SignalMetricEvent =
  | 'emitted'
  | 'seen'
  | 'acted'
  | 'dismissed'
  | 'snoozed'
  | 'resolved'
  // HONE — the neutral "✕ / set aside"; recorded for audit, EXCLUDED from noise tuning (like `resolved`).
  | 'setAside'
  // TUNE U10 — bumped by the uniform feedback capture (FeedbackService), not the lifecycle mutators.
  | 'useful'
  | 'notUseful'
  | 'actionGood'
  | 'actionBad';

/**
 * RECON R10 — the metric events that count as "this signal kind was noise" for the future noise-tuning /
 * autonomy consumer (SIG-07). `dismissed` (user rejected it) and `snoozed` (not now) tune what surfaces;
 * `resolved` (you handled the source out-of-band) is NEUTRAL and MUST be excluded, and `acted`/`seen`/
 * `emitted` are engagement/volume, not negatives. Canonical selector so SIG-07 has one place to consume
 * — and T-00 has one thing to assert (excludes `resolved`, includes `dismissed`).
 */
export const TUNING_NEGATIVE_EVENTS: readonly SignalMetricEvent[] = ['dismissed', 'snoozed', 'notUseful'];

/** Whether a metric event feeds the noise-tuning loop as a negative (RECON R10). Excludes `resolved`. */
export function isTuningNegativeSignal(event: SignalMetricEvent): boolean {
  return TUNING_NEGATIVE_EVENTS.includes(event);
}

// --- Pure helpers -------------------------------------------------------------------------------

/** Display rank: lower sorts first. urgent(0) → action(1) → info(2) (spec D12). */
export const PRIORITY_RANK: Record<SignalPriority, number> = { urgent: 0, action: 1, info: 2 };

/**
 * Deterministic dedupe key (spec D9). One live signal per (kind, subject): re-emitting the same
 * subject upserts rather than duplicating. `task-nudge` keys on the template/task id so the nightly
 * planner re-dates instead of stacking.
 */
export function signalDedupeKey(kind: SignalKind, entityRef: string): string {
  return `${kind}:${entityRef}`;
}

/** Order signals for the feed: priority (urgent→info), then newest first, then id for stability (D12). */
export function compareSignals(a: Signal, b: Signal): number {
  const byPriority = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
  if (byPriority !== 0) return byPriority;
  if (a.createdAt !== b.createdAt) return b.createdAt - a.createdAt;
  return a.signalId.localeCompare(b.signalId);
}

/** A defensively-sorted copy for display. */
export function sortSignalsForDisplay(signals: Signal[]): Signal[] {
  return [...signals].sort(compareSignals);
}

/** True once a signal's kind-specific TTL has elapsed (spec D17). Expired signals are hidden/dismissed. */
export function isExpired(signal: Pick<Signal, 'expiresAt'>, now: number): boolean {
  return signal.expiresAt != null && now >= signal.expiresAt;
}

/**
 * Whether an unacted signal from a PAST day should also surface on today (spec D5). Only `active`,
 * unexpired `urgent`/`action` signals roll forward; `info` never does, and dismissed/acted/snoozed
 * never do. A snoozed signal resurfaces via `snoozedUntil`, not roll-forward.
 */
export function rollsForward(signal: Signal, now: number): boolean {
  if (signal.status !== 'active') return false;
  if (isExpired(signal, now)) return false;
  return signal.priority === 'urgent' || signal.priority === 'action';
}

/** Whether a snoozed signal is due to resurface (its snooze window has elapsed). */
export function isSnoozeElapsed(signal: Pick<Signal, 'status' | 'snoozedUntil'>, now: number): boolean {
  return signal.status === 'snoozed' && signal.snoozedUntil != null && now >= signal.snoozedUntil;
}

// --- SETTLE-00 — the resolution contract (the core invariant) ------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * SETTLE-00 — every signal must declare a RESOLUTION CONTRACT so "lingering" is an impossible state
 * (spec §3). A signal is either:
 *   - `bound` — it reconciles to a live external source (an email thread / event / bill / shipment /
 *     decision). It clears when that source is handled (RECON/MIRROR + the SETTLE resolver registry) or,
 *     for a few kinds, by the producer re-emitting a past-TTL card (pending-delivery on `delivered`).
 *   - `ephemeral` — a recap/digest with no external source to reconcile. It clears once SEEN (DL-6/DL-14):
 *     tier-A (`seen-once`) low-stakes lines clear on the first dwell; tier-B (`seen-twice`) high-value
 *     recaps require two distinct viewing sessions so a fast scroll-by can't retire them. Both carry a
 *     `ttlMs` backstop for cards never seen.
 *
 * The `Record<SignalKind, …>` shape makes classification EXHAUSTIVE: a new `SignalKind` won't compile
 * until it's added here (spec T-00). `clearsWhenFor`/`isBoundKind` read this at runtime.
 */
export type EphemeralClearsWhen = 'seen-once' | 'seen-twice';

export type SignalContract =
  | { family: 'bound' }
  | { family: 'ephemeral'; clearsWhen: EphemeralClearsWhen; ttlMs: number };

/** Tier-A ephemeral backstop TTL (low-stakes recaps) when the producer set no `expiresAt`. */
export const EPHEMERAL_TIER_A_TTL_MS = 2 * DAY_MS;
/** Tier-B ephemeral backstop TTL (high-value recaps) when the producer set no `expiresAt`. */
export const EPHEMERAL_TIER_B_TTL_MS = 7 * DAY_MS;

/**
 * The classification of every {@link SignalKind} (spec §4.1, DL-14). Bound kinds reconcile to a source;
 * ephemeral kinds clear on seen with a TTL backstop. Exhaustive by construction (the Record type).
 */
export const SIGNAL_KIND_CONTRACT: Record<SignalKind, SignalContract> = {
  // --- Bound (reconcile to a live source) — spec §4.1 --------------------------------------------
  'action-proposed': { family: 'bound' },
  'action-escalated': { family: 'bound' },
  'bill-due': { family: 'bound' },
  'bill-needs-you': { family: 'bound' },
  'bill-paid': { family: 'bound' },
  'financial-alert': { family: 'bound' },
  'slack-needs-reply': { family: 'bound' },
  'pending-delivery': { family: 'bound' },
  'tracking-found': { family: 'bound' },
  'decision-revisit-due': { family: 'bound' },
  'decision-updated': { family: 'bound' },
  'graduation-proposal': { family: 'bound' },
  'shopping-attention': { family: 'bound' },
  'order-placed': { family: 'bound' },
  // SENTRY — bound to the event that tripped the watch (email thread / calendar event / charge / shipment).
  'watch-fired': { family: 'bound' },
  // RELAY — bound to the Job; clears when it leaves needs-review.
  'job-needs-review': { family: 'bound' },
  // KUDOS — bound to the suggestion; clears when the thank-you is written (acted) or dismissed.
  'kudos-suggested': { family: 'bound' },
  // IMPL-00.7 deviation from spec §4.1: `task-nudge` is BOUND, not ephemeral. A deadline reminder that
  // vanished on a mere glance would defeat its purpose; it must persist until the deadline passes / the
  // task is done / it's dismissed (its existing lifecycle via the nightly deadline planner + TTL). Flagged
  // for owner review in the decision log.
  'task-nudge': { family: 'bound' },
  // --- Ephemeral tier A (seen-once, low-stakes) — spec §4.1 --------------------------------------
  'triage-audit': { family: 'ephemeral', clearsWhen: 'seen-once', ttlMs: EPHEMERAL_TIER_A_TTL_MS },
  'ingest-filtered': { family: 'ephemeral', clearsWhen: 'seen-once', ttlMs: EPHEMERAL_TIER_A_TTL_MS },
  'ingest-think': { family: 'ephemeral', clearsWhen: 'seen-once', ttlMs: EPHEMERAL_TIER_A_TTL_MS },
  'amazon-match-digest': { family: 'ephemeral', clearsWhen: 'seen-once', ttlMs: EPHEMERAL_TIER_A_TTL_MS },
  'task-added': { family: 'ephemeral', clearsWhen: 'seen-once', ttlMs: EPHEMERAL_TIER_A_TTL_MS },
  'note-added': { family: 'ephemeral', clearsWhen: 'seen-once', ttlMs: EPHEMERAL_TIER_A_TTL_MS },
  'call-failed': { family: 'ephemeral', clearsWhen: 'seen-once', ttlMs: EPHEMERAL_TIER_A_TTL_MS },
  // KUDOS — a gentle "who is this card for?" confirm nudge; re-emitted nightly if still unresolved.
  'kudos-reconcile': { family: 'ephemeral', clearsWhen: 'seen-once', ttlMs: EPHEMERAL_TIER_A_TTL_MS },
  // --- Ephemeral tier B (seen-twice, high-value) — spec §4.1 / DL-14 ----------------------------
  'autonomy-recap': { family: 'ephemeral', clearsWhen: 'seen-twice', ttlMs: EPHEMERAL_TIER_B_TTL_MS },
  'decisions-logged': { family: 'ephemeral', clearsWhen: 'seen-twice', ttlMs: EPHEMERAL_TIER_B_TTL_MS },
  'guidance-digest': { family: 'ephemeral', clearsWhen: 'seen-twice', ttlMs: EPHEMERAL_TIER_B_TTL_MS },
  'routing-proposal': { family: 'ephemeral', clearsWhen: 'seen-twice', ttlMs: EPHEMERAL_TIER_B_TTL_MS },
  'resurface-digest': { family: 'ephemeral', clearsWhen: 'seen-twice', ttlMs: EPHEMERAL_TIER_B_TTL_MS },
  'call-completed': { family: 'ephemeral', clearsWhen: 'seen-twice', ttlMs: EPHEMERAL_TIER_B_TTL_MS },
};

/** SETTLE-00 — the resolution contract for a signal kind. */
export function contractFor(kind: SignalKind): SignalContract {
  return SIGNAL_KIND_CONTRACT[kind];
}

/** SETTLE-00 — whether a kind reconciles to a live source (vs an ephemeral recap). */
export function isBoundKind(kind: SignalKind): boolean {
  return SIGNAL_KIND_CONTRACT[kind].family === 'bound';
}

/** SETTLE-00 — how many distinct viewing sessions retire an ephemeral kind (1 for tier A, 2 for tier B). */
export function ephemeralSeenThreshold(kind: SignalKind): number | null {
  const c = SIGNAL_KIND_CONTRACT[kind];
  if (c.family !== 'ephemeral') return null;
  return c.clearsWhen === 'seen-twice' ? 2 : 1;
}

/**
 * SETTLE-00 — an ephemeral card should clear once its `seenCount` reaches its tier threshold (DL-6/DL-14).
 * Bound kinds never clear this way (they reconcile to their source). Pure — the service applies it.
 */
export function shouldClearOnSeen(signal: Pick<Signal, 'kind' | 'seenCount'>): boolean {
  const threshold = ephemeralSeenThreshold(signal.kind);
  if (threshold == null) return false;
  return (signal.seenCount ?? 0) >= threshold;
}

/**
 * SETTLE-00 — the emit-time TTL backstop for an ephemeral kind when the producer set no `expiresAt`, so a
 * never-seen recap still ages out. `null` for bound kinds (no imposed backstop). Pure.
 */
export function ephemeralTtlBackstop(kind: SignalKind): number | null {
  const c = SIGNAL_KIND_CONTRACT[kind];
  return c.family === 'ephemeral' ? c.ttlMs : null;
}

/**
 * SETTLE — the sending domain SETTLE may offer to suppress wholesale (DL-2 / DL-9-domain, "org-domain
 * only + blocklist"). Only when the identity is DKIM-authenticated (`dkim:<domain>`) AND the domain isn't
 * a shared freemail/ESP provider. A `sender:<addr>` identity (freemail, unauthenticated, or shared-ESP
 * fallback) returns null — those can only ever be suppressed by exact address. Reuses {@link isFreemailDomain}.
 */
export function suppressibleDomainOf(identityKey: string): string | null {
  if (!identityKey.startsWith('dkim:')) return null;
  const domain = identityKey.slice('dkim:'.length).toLowerCase().trim();
  if (!domain || isFreemailDomain(domain)) return null;
  return domain;
}

/** SETTLE-04 — the disposition-policy key for a wholesale domain suppression (DL-2). */
export function domainSuppressKey(domain: string): string {
  return `domain:${domain.toLowerCase().trim()}`;
}

/**
 * SETTLE-04 — the sending domain an identity key belongs to, for domain graduation (DL-2). Handles both
 * `dkim:<domain>` and `sender:<local@domain>`. Returns '' when it can't be determined.
 */
export function domainOfIdentity(identityKey: string): string {
  if (identityKey.startsWith('dkim:')) return identityKey.slice('dkim:'.length).toLowerCase().trim();
  if (identityKey.startsWith('sender:')) return domainOf(identityKey.slice('sender:'.length));
  if (identityKey.startsWith('domain:')) return identityKey.slice('domain:'.length).toLowerCase().trim();
  return '';
}
